import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
    ArrowLeft, BarChart3, TrendingUp, Clock, CheckCircle, 
    XCircle, Activity, Download, FileText, PieChart, Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = '/logo.png';

const conditionLabels = {
    hip_arthritis: 'Hip Arthritis',
    knee_arthritis: 'Knee Arthritis',
    shoulder_arthritis: 'Shoulder Arthritis',
    rotator_cuff_tear: 'Rotator Cuff',
    spinal_stenosis: 'Spinal Stenosis',
    carpal_tunnel_syndrome: 'Carpal Tunnel',
    hallux_valgus: 'Hallux Valgus'
};

export default function AnalyticsPage() {
    const navigate = useNavigate();
    const { authToken, user } = useAuth();
    const [analytics, setAnalytics] = useState(null);
    const [waitTimes, setWaitTimes] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month');

    useEffect(() => {
        if (authToken) {
            fetchAnalytics();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, authToken]);

    const fetchAnalytics = async () => {
        if (!authToken) {
            return;
        }
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            const [overviewRes, waitTimesRes] = await Promise.all([
                axios.get(`${API}/analytics/overview?period=${period}`, { headers }),
                axios.get(`${API}/analytics/wait-times`, { headers }).catch(() => ({ data: null }))
            ]);
            setAnalytics(overviewRes.data);
            setWaitTimes(waitTimesRes.data);
        } catch (error) {
            console.error('Analytics error:', error);
            toast.error('Failed to load analytics');
        } finally {
            setLoading(false);
        }
    };

    const exportReferralsCSV = async () => {
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            const response = await axios.get(`${API}/export/referrals/csv`, { 
                headers,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `referrals_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success('Referrals exported successfully');
        } catch (error) {
            toast.error('Export failed');
        }
    };

    const exportAuditCSV = async () => {
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            const response = await axios.get(`${API}/export/audit-trail/csv`, { 
                headers,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit_trail_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success('Audit trail exported successfully');
        } catch (error) {
            toast.error('Export failed');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <Activity className="w-8 h-8 animate-spin text-teal-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50" data-testid="analytics-page">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Dashboard
                        </Button>
                        <div className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="FendanaCor" className="w-8 h-8 rounded-lg" />
                            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Analytics Dashboard
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={exportReferralsCSV}
                                data-testid="export-csv-btn"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Export Referrals
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={exportAuditCSV}
                                data-testid="export-audit-btn"
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                Audit Trail
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header with Period Selector */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 flex justify-between items-start"
                >
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Service Performance Analytics
                        </h1>
                        <p className="text-slate-500 mt-1">
                            NHS referral volume, acceptance rates, and condition trends
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {['week', 'month', 'quarter', 'year'].map(p => (
                            <Button
                                key={p}
                                variant={period === p ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setPeriod(p)}
                                className={period === p ? 'bg-teal-600' : ''}
                            >
                                {p.charAt(0).toUpperCase() + p.slice(1)}
                            </Button>
                        ))}
                    </div>
                </motion.div>

                {/* Key Metrics */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
                >
                    <Card className="border-t-4 border-t-teal-600">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Total Referrals</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">
                                        {analytics?.total_referrals || 0}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {analytics?.average_per_day?.toFixed(1) || 0} avg/day
                                    </p>
                                </div>
                                <BarChart3 className="w-8 h-8 text-teal-600" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-t-4 border-t-emerald-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Acceptance Rate</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">
                                        {analytics?.acceptance_rate?.toFixed(1) || 0}%
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {analytics?.accepted || 0} accepted
                                    </p>
                                </div>
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-t-4 border-t-rose-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Rejected</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">
                                        {analytics?.rejected || 0}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        Did not meet criteria
                                    </p>
                                </div>
                                <XCircle className="w-8 h-8 text-rose-600" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-t-4 border-t-amber-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Pending Review</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">
                                        {analytics?.by_status?.pending || 0}
                                    </p>
                                </div>
                                <Activity className="w-8 h-8 text-amber-600" />
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Status Distribution */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <Card className="h-full">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <PieChart className="w-5 h-5 text-teal-600" />
                                    Referral Status Distribution
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {Object.entries(analytics?.by_status || {}).map(([status, count]) => {
                                        const total = analytics?.total_referrals || 1;
                                        const percentage = ((count / total) * 100).toFixed(1);
                                        const colors = {
                                            pending: 'bg-amber-500',
                                            accepted: 'bg-emerald-500',
                                            rejected: 'bg-rose-500',
                                            screening_rejected: 'bg-red-400',
                                            scheduled: 'bg-blue-500',
                                            queued: 'bg-indigo-500',
                                            tests_requested: 'bg-purple-500'
                                        };
                                        return (
                                            <div key={status}>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="capitalize text-slate-700">{status.replace(/_/g, ' ')}</span>
                                                    <span className="font-medium">{count} ({percentage}%)</span>
                                                </div>
                                                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full ${colors[status] || 'bg-slate-400'} rounded-full transition-all`}
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Urgency Distribution */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                    >
                        <Card className="h-full">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <TrendingUp className="w-5 h-5 text-teal-600" />
                                    Urgency Level Distribution
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {Object.entries(analytics?.by_urgency || {}).map(([level, count]) => {
                                        const total = analytics?.total_referrals || 1;
                                        const percentage = ((count / total) * 100).toFixed(1);
                                        const colors = {
                                            urgent: 'bg-rose-500',
                                            moderate: 'bg-amber-500',
                                            semi_urgent: 'bg-amber-500',
                                            routine: 'bg-emerald-500'
                                        };
                                        const targets = {
                                            urgent: '6 weeks',
                                            semi_urgent: '13 weeks',
                                            routine: '26 weeks'
                                        };
                                        return (
                                            <div key={level}>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="capitalize text-slate-700">
                                                        {level.replace(/_/g, ' ')}
                                                        {targets[level] && <span className="text-slate-400 ml-2">({targets[level]})</span>}
                                                    </span>
                                                    <span className="font-medium">{count} ({percentage}%)</span>
                                                </div>
                                                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className={`h-full ${colors[level] || 'bg-slate-400'} rounded-full transition-all`}
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Top Conditions */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Card>
                            <CardHeader>
                                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Top Conditions
                                </CardTitle>
                                <CardDescription>
                                    Most frequently referred conditions
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {Object.entries(analytics?.by_condition || {}).slice(0, 5).map(([condition, count], index) => (
                                        <div 
                                            key={condition}
                                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                                    index === 0 ? 'bg-teal-100 text-teal-700' :
                                                    index === 1 ? 'bg-slate-200 text-slate-700' :
                                                    'bg-slate-100 text-slate-600'
                                                }`}>
                                                    {index + 1}
                                                </span>
                                                <span className="text-sm font-medium text-slate-700">
                                                    {conditionLabels[condition] || condition.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                            <span className="text-sm font-bold text-slate-900">
                                                {count}
                                            </span>
                                        </div>
                                    ))}
                                    {(!analytics?.by_condition || Object.keys(analytics.by_condition).length === 0) && (
                                        <p className="text-center text-slate-500 py-4">No data available</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Daily Trend */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                    >
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <Calendar className="w-5 h-5 text-teal-600" />
                                    Daily Volume Trend
                                </CardTitle>
                                <CardDescription>
                                    Referral volume over time
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {(analytics?.daily_trend || []).slice(-7).map((item) => {
                                        const maxCount = Math.max(...(analytics?.daily_trend || []).map(v => v.count)) || 1;
                                        const percentage = (item.count / maxCount) * 100;
                                        return (
                                            <div key={item.date}>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-slate-600">{item.date}</span>
                                                    <span className="font-medium">{item.count}</span>
                                                </div>
                                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full bg-teal-500 rounded-full transition-all"
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {(!analytics?.daily_trend || analytics.daily_trend.length === 0) && (
                                        <p className="text-center text-slate-500 py-4">No data available</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>

                    {/* Wait Times */}
                    {waitTimes && Object.keys(waitTimes?.wait_times || {}).length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="lg:col-span-2"
                        >
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        <Clock className="w-5 h-5 text-teal-600" />
                                        Wait Time Performance
                                    </CardTitle>
                                    <CardDescription>
                                        Average wait times vs NHS targets
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {Object.entries(waitTimes?.wait_times || {}).map(([urgency, data]) => (
                                            <div key={urgency} className="p-4 bg-slate-50 rounded-lg">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium capitalize">{urgency.replace(/_/g, ' ')}</span>
                                                    <Badge variant={data.meeting_target ? 'default' : 'destructive'}>
                                                        {data.meeting_target ? 'On Target' : 'Behind Target'}
                                                    </Badge>
                                                </div>
                                                <div className="text-2xl font-bold text-slate-900">
                                                    {data.average_days} days
                                                </div>
                                                <div className="text-sm text-slate-500">
                                                    Target: {data.target_days} days
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </div>
            </main>
        </div>
    );
}
